import type { Question, Section } from "./data"
import { type AnswerEvent, getHistory } from "./storage"

// ============================================================
// SELECTION ALGORITHM
// One sampler serves every quiz mode: `selectBlueprinted` draws N
// questions from a candidate pool by stratifying the practice-test
// blueprint proportions onto N, then filling each section's quota by
// weighted sampling under spread caps. The caps keep a single quiz
// from repeating a handbook passage (page), a subsection (topic), or
// a variant root, and they relax in a fixed order when a quota cannot
// fill. The weight hook is how modes differ: test modes pass a
// uniform weight, the Study Quiz passes `dueScore`.
// ============================================================

// Target per-section mix for a 25-question practice test. Any other
// test size derives its quotas from these proportions, floors plus a
// remainder lottery. See Documentation/conventions.md.
const BLUEPRINT: Record<Section, number> = { 1: 2, 2: 2, 3: 2, 4: 1, 5: 1, 6: 6, 7: 5, 8: 2, 9: 3, 10: 1 }

const UNSEEN_SCORE = 100
const MASTERED_SCORE = 0.1

// Tracks question ids seen during the current session so the selector
// doesn't immediately re-pick what just showed up.
export const seenThisSession = new Set<string>()

// Study-mode weight. Each question scores into one of three tiers:
//   Unseen   — never attempted, top priority
//   Active   — seen but not mastered, weighted by miss rate and recency
//   Mastered — recent correct streak, deprioritized to near zero
export function dueScore(q: Question): number {
  const events = getHistory(q)
  if (events.length === 0) return UNSEEN_SCORE
  if (isMastered(events)) return MASTERED_SCORE

  const wrongCount = events.filter((e) => !e.correct).length
  const missRate = wrongCount / events.length
  const lastWrong = !events[events.length - 1].correct
  const recencyBoost = lastWrong ? 1.5 : 0

  return 1 + missRate * 2 + recencyBoost
}

function isMastered(events: AnswerEvent[]): boolean {
  if (events.length < 3) return false
  return events.slice(-3).every((e) => e.correct)
}

// Variant linkage: a parent question and any "alt-answers" or "paired"
// derivations share a root id. The sampler never picks two questions
// with the same root, so a quiz cannot include both halves of a pair.
export function rootId(q: Question): string {
  return q.generated?.parent ?? q.id
}

function pageKey(q: Question): string {
  return `${q.answer.section}:${q.answer.page}`
}

function topicKey(q: Question): string {
  return `${q.answer.section}:${q.answer.topic}`
}

// Spread caps, relaxed in order when a fill runs dry. The root cap is
// hard and never relaxes.
type CapLevel = "strict" | "page-only" | "root-only"
const CAP_LADDER: CapLevel[] = ["strict", "page-only", "root-only"]

interface UsedKeys {
  roots: Set<string>
  pages: Set<string>
  topics: Set<string>
}

function violates(q: Question, used: UsedKeys, level: CapLevel): boolean {
  if (used.roots.has(rootId(q))) return true
  if (level !== "root-only" && used.pages.has(pageKey(q))) return true
  if (level === "strict" && used.topics.has(topicKey(q))) return true
  return false
}

function markUsed(q: Question, used: UsedKeys) {
  used.roots.add(rootId(q))
  used.pages.add(pageKey(q))
  used.topics.add(topicKey(q))
}

// Draws one index from `items` with probability proportional to each
// entry's weight. Falls back to the last index on rounding shortfall.
function weightedIndex(items: { w: number }[]): number {
  const total = items.reduce((s, x) => s + x.w, 0)
  let r = Math.random() * total
  let idx = 0
  for (; idx < items.length - 1; idx++) {
    r -= items[idx].w
    if (r <= 0) break
  }
  return idx
}

// Weighted sample without replacement from `pool`, skipping candidates
// that violate the caps at `level`. Mutates `used` as it picks.
function pickWeighted(
  pool: Question[],
  n: number,
  weight: (q: Question) => number,
  used: UsedKeys,
  level: CapLevel,
): Question[] {
  const entries = pool.map((q) => ({ q, w: weight(q) })).filter((e) => e.w > 0)
  const picked: Question[] = []
  while (picked.length < n) {
    const avail = entries.filter((e) => !violates(e.q, used, level))
    if (avail.length === 0) break
    const chosen = avail[weightedIndex(avail)].q
    markUsed(chosen, used)
    picked.push(chosen)
    entries.splice(
      entries.findIndex((e) => e.q === chosen),
      1,
    )
  }
  return picked
}

// Projects the blueprint proportions onto `n` slots across the sections
// present in the pool. Each section takes the floor of its proportional
// share, and the leftover slots go out by a weighted lottery over the
// fractional remainders, so every draw lands within one question of the
// share, the expected mix equals the blueprint exactly, and sections
// whose share truncates to zero at small n still rotate in.
function apportion(n: number, bySection: Map<Section, Question[]>): Map<Section, number> {
  const present = [...bySection.keys()]
  const total = present.reduce((s, sec) => s + BLUEPRINT[sec], 0)
  const quotas = new Map<Section, number>()
  const lots: { sec: Section; w: number }[] = []
  for (const sec of present) {
    const share = (n * BLUEPRINT[sec]) / total
    quotas.set(sec, Math.floor(share))
    if (share > Math.floor(share)) lots.push({ sec, w: share - Math.floor(share) })
  }
  let remaining = n - [...quotas.values()].reduce((s, v) => s + v, 0)
  while (remaining > 0 && lots.length > 0) {
    const idx = weightedIndex(lots)
    const { sec } = lots[idx]
    quotas.set(sec, (quotas.get(sec) ?? 0) + 1)
    lots.splice(idx, 1)
    remaining--
  }
  return quotas
}

// Draws up to `n` questions from `candidates`. The blueprint shapes the
// per-section mix, the caps spread picks across passages, and `weight`
// ranks candidates within a section. Guarantees `n` questions whenever
// the pool holds `n` distinct variant roots: after the stratified fill,
// a global fill walks the cap ladder, first over unseen-this-session
// candidates and then over recycled ones.
export function selectBlueprinted(
  candidates: Question[],
  n: number,
  weight: (q: Question) => number = () => 1,
): Question[] {
  const fresh = candidates.filter((q) => !seenThisSession.has(q.id))
  const recycled = candidates.filter((q) => seenThisSession.has(q.id))
  const used: UsedKeys = { roots: new Set(), pages: new Set(), topics: new Set() }
  const picked: Question[] = []

  const bySection = new Map<Section, Question[]>()
  for (const q of fresh) {
    const list = bySection.get(q.answer.section) ?? []
    list.push(q)
    bySection.set(q.answer.section, list)
  }
  if (bySection.size > 0) {
    for (const [section, quota] of apportion(n, bySection)) {
      picked.push(...pickWeighted(bySection.get(section) ?? [], quota, weight, used, "strict"))
    }
  }

  for (const pool of [fresh, recycled]) {
    for (const level of CAP_LADDER) {
      if (picked.length >= n) return picked
      picked.push(...pickWeighted(pool, n - picked.length, weight, used, level))
    }
  }
  return picked
}

// Resolve the handbook reference for a question.
export function buildRef(q: Question) {
  return {
    section: q.answer.section,
    topic: q.answer.topic,
    page: q.answer.page,
  }
}
